import axios from 'axios'
import PropTypes from 'prop-types'
import history from 'router/history'
import i18next from 'i18next'
import { refreshAccount } from 'state/Account'
import { setPrivilegesJWT } from 'state/Auth'
import { refreshRestaurants } from 'state/Restaurants'
import HTTP_CODE from 'utils/HTTP_CODE'
import platform from 'utils/platform'
import { loading } from './Request'
import { store } from './_store'
import { Alerts, currentVersion } from '../utils'

/** @var {object} window.ENV Comes from the Cordova build (see manager/www/env.js and hooks/before_prepare.js at the app repo) */
/** @var {string} window.ENV.ROOT_URL The app URL that should be used by the native app */

// noinspection JSValidateTypes - otherwise, it will complain that `axios.create()` is not really returning OUR instance
/** @type {ManagerAPI} */
const API = axios.create({
	//the webapp don't need this, as it's being served from the correct URL;
	//the app is served from the local filesystem, and thus it needs complete URLs
	baseURL: (window.IS_APP? window.ROOT_URL : '')+process.env.REACT_APP_API_PATH
})

/**
 * List of "loadable" API resources, mainly used at {@link getLoading}
 * @type {object}
 */
API.RESOURCES = Object.freeze({
	ORDERS      : 'order',
	ITEMS       : 'item',
	CATEGORIES  : 'category',
	INVOICES    : 'invoice',
	ACCOUNT     : 'account',
	RESTAURANTS : 'restaurant',
	DELIVERY    : 'delivery',
	FORCED      : 'FORCED',
})
API.PROPTYPE_RESOURCES = PropTypes.oneOfType([
	PropTypes.arrayOf(PropTypes.oneOf(Object.values(API.RESOURCES))),
	PropTypes.oneOf(Object.values(API.RESOURCES))
])

function dispatchResourceLoading(config, status) {
	const resource = config.url.split(/[/?]/)[0]
	store.dispatch(loading({ resource, status }))
}

API.interceptors.request.use(config => {
	if (!navigator.onLine) {
		throw new axios.Cancel('offline')
	}

	dispatchResourceLoading(config, true)

	//this is a dup with the current auth cookie, but it's needed in iOS (CORS issues? check c53d5f4)
	//TODO should we drop the cookie auth and just use the header instead, then? if so, the swagger explorer will need fixing
	const token = store.getState().auth.token
	if (token) {
		config.headers['Authorization'] = `Bearer ${token}`
	}

	if (localStorage.ImAnIPad === undefined) {
		localStorage.ImAnIPad = platform.isReallyAnIPad()? 'oh yeah' : '' //let's cache this, so we're not re-sniffing with _every_ request
	}
	if (localStorage.ImAnIPad) {
		config.headers['X-I-am-an-iPad'] = 'Apple won\'t kid us'
	}

	//empties a standard header from Android WebViews that is being deprecated (https://is.gd/KdzTnc) and is useless for us anyway, given iOS doesn't send the package name on it as well (can't remove it completely because it's added later by Android, so the best we can do is set it empty, it seems Android doesn't try to override it)
	config.headers['X-Requested-With'] = ''

	//FIXME I guess we should start limiting this header to be sent only infrequently, it's getting quite bloated...
	//it looks like we've already been here; since this header only changes on new versions, no need to recalculate it
	if ('X-App-Version' in config.headers) {
		return config
	}

	config.headers['X-App-Version'] = prepareAnalyticsHeader()

	return config
})

function prepareAnalyticsHeader() {
	let details = {
		v     : currentVersion(),
		size  : `${window.innerWidth}x${window.innerHeight}`,
	}

	if (window.localStorage.reduxVersion) { //comes from _store.js
		details.redux = window.localStorage.reduxVersion
	}

	if ('CORDOVA_VERSION' in window) { //comes from Cordova's www/index.html
		details.flavor = window.CORDOVA_VERSION

		//for some reason, tne Cordova User-Agent is more specific (with the correct Android version and device model) than Android's Chrome UA; thus, the UA received by the server is not reliable enough to identify OS and device details, so we're using the Cordova.device details when available
		if ('OS_VERSION' in window) { //added in 2.4.0; TODO remove this check when there's no 2.3.x in use anymore
			details.os = `v${window.OS_VERSION}`
			details.device = window.DEVICE_MODEL //FIXME check if we could use this to skip the separate iPad header
		}
	} else if ('cordova' in window) {
		details.flavor = 'unrecognized.cordova' //TODO this should also be gone from 2.4 on, but check stats for sniffed flavors before removing
	} else {
		details.flavor = 'web'
	}

	return new URLSearchParams(details).toString()
}

function interceptAnyResponse(resp, config = null) {
	dispatchResourceLoading(config || resp.config, false)
	if (typeof resp == 'object' && 'headers' in resp && 'x-suggested-refresh' in resp.headers) {
		const refreshes = resp.headers['x-suggested-refresh'].split('; ')
		for (let refresh of refreshes) {
			let [type, payload] = refresh.split('=')
			switch (type) { //can't use server constants because they're a hook...........
				case 'PRIV':
					store.dispatch(setPrivilegesJWT(payload))
					break

				case 'ACC':
					store.dispatch(refreshAccount)
					break

				case 'REST':
					store.dispatch(refreshRestaurants)
					break

				case 'ORDER': // eslint-disable-line no-fallthrough
				case 'CUST':
				default:
					console.error(`HEADER REFRESH OF '${type}' NOT IMPLEMENTED YET!`, { type, payload })
			}
		}
	}
}

API.interceptors.response.use(
	resp => {
		interceptAnyResponse(resp)

		if (resp.status == HTTP_CODE.OK__200 && typeof resp.data == 'object' && 'errors' in resp.data) {
			//probably an early PHP error on the backend server
			console.error('Wait, what? HTTP 200 Error??? 😱 Please, fix this if it\'s intentional 😭')
			for (let error of resp.data.errors) {
				Alerts.danger(`${error.type || i18next.t('Unknown error')}: ${error.message}`)
			}
			return Promise.reject(resp)
		}

		return Promise.resolve(resp)
	},
	e => {
		interceptAnyResponse(e.response, e.config) //happens first, in case we have trouble finding the error msg

		if (e.message == 'offline') {
			Alerts.warning(i18next.t('It looks like you\'re offline. Try again later when your connection is restored.'))
			return Promise.reject(e)
		}

		const showError = msg => Alerts.error(msg, { perm: e.config.__PERM_ERRORS || false })

		//https://github.com/axios/axios#handling-errors
		if (e.response) {
			if (shouldWeHandleTheError(e.response)) {
				let error = (e.response.data && e.response.data.error) || false
				let msg     = ''
				switch (e.response.status) {
					case HTTP_CODE.UNAUTHORIZED__401:
						//FIXME skip reporting this to Sentry? why are people having this, can we discover before dropping it?
						showError(error.message != 'Unauthorized'? error.message : i18next.t('Sorry, your login details expired. Please login again.'))
						history.push('/logout')
						break

					case HTTP_CODE.FORBIDDEN__403:
						msg = error.message.toLowerCase() == 'forbidden'? '' : `: ${error.message.substr('forbidden: '.length)}`
						showError(i18next.t('Insufficient privileges for this action') + msg)
						break

					case HTTP_CODE.UPGRADE_REQUIRED__426:
						showError(
							platform.isNative()?
								i18next.t('It looks like this app is outdated. Try closing it completely (remove from the list of recent apps), then reopen.') :
								i18next.t('It looks like this page is outdated. Please, refresh all Manager tabs you might have open')
						)
						break

					default:
						console.error(`[API.js] Unexpected HTTP ${e.response.status} ${e.response.statusText}`, e.response)
						if (e.response.status == HTTP_CODE.BAD_GATEWAY__502) { //FIXME expected to be removed with sc-37508
							if (error.message) {
								error.message = error.message.replace(/bad gateway: /i, '')
							} else {
								//probably a VERY BAD™ error, this is probably an HTML response
								error = { message: 'Bad gateway' }
							}
						}
						//replace double dots in case the API responds with one too (happens with some messages, but not all)
						msg = `${error.message}.`.replace(/([.。])\.$/, '$1') || i18next.t('Sorry! We got an error.')
						showError(`${msg} ` + i18next.t('CONTACT_TRY_AGAIN'))
				}
			}
		} else if (e.request) {
			if (shouldWeHandleTheError()) {
				//the issue probably happened before we could get any response
				console.error('API error', e) //XMLHttpRequest

				let msg
				if (e.message.toLowerCase() == 'network error') {
					msg = i18next.t('Your connection is unstable; please, try again.') //skipped at Sentry.init({ ignoreErrors })
				} else {
					msg = i18next.t('It was not possible to finish this action.')+' '+i18next.t('CONTACT_TRY_AGAIN')
				}
				showError(msg)
			}
		} else {
			// Something happened in setting up the request that triggered an Error
			console.error('Error', e.message)
			showError(e.message)
		}

		return Promise.reject(e) //the implementer will only need to handle this if further action is required
})

/**
 * Syntactic sugar to make the copied methods a tad smaller.
 * @param errorConfig {SkipErrorCodes|ErrorHandlingObj}
 * @param axiosConfig {object}
 * @returns {ManagerRequestConfig|object}
 */
const mergeErrCfg = (errorConfig, axiosConfig) => {
	if (typeof errorConfig == 'object' && !Array.isArray(errorConfig)) {
		return Object.assign({
			__DONT_HANDLE: errorConfig.codesToSkip,
			__PERM_ERRORS: errorConfig.permanentErrors,
		}, axiosConfig)
	}
	return Object.assign({ __DONT_HANDLE: errorConfig }, axiosConfig)
}

//Types are defined in API.d.ts, because it's the only way to merge in the Axios TS docs
API.getAndHandle = (path, errorConfig, cfg = {}) => API.get(path, mergeErrCfg(errorConfig, cfg))
API.headAndHandle = (path, errorConfig, cfg = {}) => API.head(path, mergeErrCfg(errorConfig, cfg))
API.deleteAndHandle = (path, errorConfig, cfg = {}) => API.delete(path, mergeErrCfg(errorConfig, cfg))
API.optionsAndHandle = (path, errorConfig, cfg = {}) => API.options(path, mergeErrCfg(errorConfig, cfg))
API.putAndHandle = (path, errorConfig, data = null, cfg = {}) => API.put(path, data, mergeErrCfg(errorConfig, cfg))
API.postAndHandle = (path, errorConfig, data = null, cfg = {}) => API.post(path, data, mergeErrCfg(errorConfig, cfg))
API.patchAndHandle = (path, errorConfig, data = null, cfg = {}) => API.patch(path, data, mergeErrCfg(errorConfig, cfg))

// noinspection JSUndefinedPropertyAssignment
/**
 * Returns the complete URL for a given URI.
 * @todo use getUri() https://github.com/axios/axios/pull/3737
 * @param uri
 * @return string
 */
API.getUrl = uri => API.defaults.baseURL + uri

/**
 * Decides whether extra config was used to skip the current error. Shouldn't be used to skip _all_ errors, since some cases should always be reported by the API class - e.g. when a request can't be fired.
 *
 * This depends on a special, home-made parameter shoved into the Axios API call config, called `__DONT_HANDLE`. The property is extra-large so it feels harmful, like React dangerous properties - because it _is_ harmful: if the error is not handled, an ugly error message will pop for the user.
 *
 * There isn't a much better place to document it, unless we possibly move to heavy TypeScript; thus, right now, it's going to be documented _here_:
 *
 * @example
 * const statusBin = 'https://httpbin.org/status'
 * //Raises "Unexpected HTTP 304" error from the API interceptor
 *   API.get(`${statusBin}/304`)
 * //This won't, unless it returns something other than 200 or 304
 *   API.getAndHandle(`${statusBin}/304`, 304)
 * //This wouldn't raise an error for 201, but will throw for 304
 *   API.postAndHandle(`${statusBin}/304`, { field1: 'aaa' }, 201)
 * //This won't raise any errors for 301 or 304 responses
 *   API.getAndHandle(`${statusBin}/301,304`, [301, 304])
 * //No errors for ANY status code. AVOID THIS!!
 *   API.getAndHandle(`${statusBin}/201,301,405`, true)
 *
 * @param resp {?ManagerResponse} Either null for unknown errors, or the Axios response (which potentially contains the special `I_WILL_HANDLE_THESE_ERRORS` - see above).
 * @return {boolean}
 */
function shouldWeHandleTheError(resp) {
	let toSkip = false
	let receivedCode

	if (typeof resp == 'object' && 'config' in resp && 'status' in resp) {
		toSkip = resp.config.__DONT_HANDLE || false
		receivedCode = resp.status
	}

	if (!receivedCode || typeof toSkip == 'boolean') {
		return !toSkip
	//the next comparisons try to handle strings as well as numbers, just in case
	} else if (Array.isArray(toSkip)) {
		return !toSkip.some(code => code == receivedCode)
	} else {
		return toSkip != receivedCode
	}
}

export default API
